import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { Mic, Upload, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

export default function VoicePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['voice-entries'],
    queryFn: () => api.getVoiceEntries({ limit: 50 }),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadVoice(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voice-entries'] });
    },
  });

  const entries = data?.data ?? [];

  const handleFile = (file: File) => {
    setFileError(null);
    if (file.type.startsWith('audio/') || file.name.match(/\.(mp3|wav|ogg|webm|m4a)$/i)) {
      uploadMutation.mutate(file);
    } else {
      setFileError(t('voice.audioOnlyError'));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const statusConfig: Record<string, { label: string; icon: any; color: string }> = {
    pending: { label: t('voice.pending'), icon: Clock, color: 'text-warning-600 bg-warning-100' },
    processing: { label: t('voice.processingStatus'), icon: Loader2, color: 'text-info-600 bg-info-100' },
    processed: { label: t('voice.completed'), icon: CheckCircle, color: 'text-success-600 bg-success-100' },
    failed: { label: t('voice.failed'), icon: XCircle, color: 'text-danger-600 bg-danger-100' },
  };

  return (
    <div className="space-y-6">
      <h1 className="page-title">{t('voice.title')}</h1>

      {/* Upload Area */}
      <div
        className={`card border-2 border-dashed transition-colors cursor-pointer ${
          dragActive ? 'border-primary-400 bg-primary-50' : 'border-surface-400/15 hover:border-primary-300'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="text-center py-8">
          {uploadMutation.isPending ? (
            <>
              <Loader2 className="mx-auto text-primary-600 mb-4 animate-spin" size={48} />
              <p className="text-lg font-medium text-surface-900">{t('voice.processing')}</p>
              <p className="text-sm text-surface-500 mt-1">{t('voice.processingNote')}</p>
            </>
          ) : (
            <>
              <Upload className="mx-auto text-surface-400 mb-4" size={48} />
              <p className="text-lg font-medium text-surface-900">{t('voice.dropzone')}</p>
              <p className="text-sm text-surface-500 mt-1">MP3, WAV, OGG, WEBM, M4A {t('voice.maxFileSize')}</p>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        />
      </div>

      {fileError && (
        <div className="card bg-danger-50 border border-danger-100">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={24} />
            <p className="text-red-800">{fileError}</p>
          </div>
        </div>
      )}

      {uploadMutation.isSuccess && (
        <div className="card bg-success-50 border border-success-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-success-600" size={24} />
            <div>
              <p className="font-medium text-green-800">
                {(uploadMutation.data as any)?.data?.message || t('voice.processSuccess')}
              </p>
              {(uploadMutation.data as any)?.data?.work_log && (
                <p className="text-sm text-success-600 mt-1">{t('voice.workLogCreated')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {uploadMutation.isError && (
        <div className="card bg-danger-50 border border-danger-100">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={24} />
            <p className="text-red-800">{t('voice.processError')}</p>
          </div>
        </div>
      )}

      {/* Voice Entries List */}
      <h2 className="text-xl font-bold text-surface-900">{t('voice.previousRecordings')}</h2>

      {isLoading && <div className="flex justify-center py-12"><div className="spinner"></div></div>}

      <div className="grid gap-4">
        {entries.length > 0 ? entries.map((entry) => {
          const config = statusConfig[entry.processing_status] || statusConfig.pending;
          const Icon = config.icon;
          return (
            <div key={entry.id} className="card hover:shadow-elevated transition-shadow">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${config.color.split(' ')[1]}`}>
                  <Mic className={config.color.split(' ')[0]} size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      {entry.transcription_text ? (
                        <p className="text-surface-900">{entry.transcription_text.substring(0, 200)}{entry.transcription_text.length > 200 ? '...' : ''}</p>
                      ) : (
                        <p className="text-surface-500 italic">{t('voice.noTranscription')}</p>
                      )}
                    </div>
                    <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                      <Icon size={16} className={entry.processing_status === 'processing' ? 'animate-spin' : ''} />
                      {config.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-surface-500 mt-2">
                    <span>{format(new Date(entry.created_at), 'dd/MM/yyyy HH:mm', { locale: he })}</span>
                    {entry.audio_duration_seconds && (
                      <><span>&bull;</span><span>{Math.round(entry.audio_duration_seconds)}s</span></>
                    )}
                    {entry.confidence && (
                      <><span>&bull;</span><span>{t('voice.confidence')}{(entry.confidence * 100).toFixed(0)}%</span></>
                    )}
                    {entry.work_log_id && (
                      <><span>&bull;</span><span className="text-success-600 font-medium">{t('voice.workLogLinked')}</span></>
                    )}
                  </div>
                  {entry.error_message && (
                    <p className="text-sm text-danger-600 mt-2">{entry.error_message}</p>
                  )}
                </div>
              </div>
            </div>
          );
        }) : !isLoading && (
          <div className="card text-center py-12">
            <Mic className="mx-auto text-surface-400 mb-4" size={48} />
            <p className="text-surface-600">{t('voice.noPrevious')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
